// src/pages/Menu/Articulacion/Admin/WorktablesHome.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import {
  listWorktables,
  createWorktable,
  deleteWorktable,
  getWorktable,
  listWorktableMinutes,
  uploadWorktableMinute,
  deleteWorktableMinute,
  updateWorktable,
  updateWorktableMinute,
} from "../../../../services/worktablesService";
import {
  Plus,
  Trash2,
  Calendar,
  ArrowLeft,
  FileText,
  ChevronRight,
  ChevronDown,
  X,
  Upload,
  Eye,
  Edit,
  Search,
  Save,
  Users,
  Mail,
  User,
} from "lucide-react";
import { Banner } from "../../../../components/common/Banner";
import SectionTips from "../../../../components/common/SectionTips";
import TooltipHelper from "../../../../components/common/TooltipHelper";
import ArrowBack from "../../../../components/common/ArrowBack";

/* ====== UI classes ====== */
const input =
  "px-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-xl transition-all";
const btnPri =
  btn +
  " bg-[#0488AF] text-white hover:bg-[#037a98] active:translate-y-[0.5px]";
const btnGhost = btn + " border bg-white hover:bg-gray-50";

/* ====== UI primitives ====== */
function GradientCard({ children, className = "", style }) {
  const hasFlexClasses = className.includes('flex') || className.includes('flex-1');
  const innerClassName = hasFlexClasses ? 'h-full flex flex-col' : '';
  
  return (
    <div
      className={`rounded-2xl p-[2px] ${className}`}
      style={{
        background:
          "linear-gradient(135deg,#E6F0FF 0%,#B8E6FF 50%,#E6FBFF 100%)",
        ...style,
      }}
    >
      <div className={`rounded-2xl bg-white ring-1 ring-black/5 shadow-sm ${innerClassName}`}>
        {children}
      </div>
    </div>
  );
}

function PlainCard({ children, className = "", style }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

function AccentCard({
  titleSlot,
  children,
  className = "",
  compactHeader = false,
}) {
  return (
    <div
      className={`rounded-2xl overflow-hidden shadow-[0_18px_38px_-18px_rgba(2,6,23,0.55)] flex flex-col ${className}`}
    >
      <div
        className={`px-5 text-white shrink-0 ${
          compactHeader ? "py-2" : "py-3"
        }`}
        style={{ background: "linear-gradient(90deg,#0c3b66,#0488AF)" }}
      >
        {titleSlot}
      </div>
      <div
        className="p-[1.5px] flex-1 flex flex-col min-h-0"
        style={{
          background:
            "linear-gradient(135deg,#E6F7FF 0%,#F4FAFF 100%)",
        }}
      >
        <div className="rounded-b-2xl bg-white flex-1 min-h-0 overflow-hidden flex flex-col">{children}</div>
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-0.5 rounded-xl bg-slate-100/70 border border-slate-200">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={[
              "px-3 h-8 rounded-lg text-xs font-medium transition focus:outline-none",
              active
                ? "text-white shadow-md border border-transparent"
                : "text-slate-700 hover:bg-white/70 border border-transparent",
            ].join(" ")}
            style={
              active
                ? {
                    background:
                      "linear-gradient(90deg,#0c3b66 0%, #0488AF 100%)",
                  }
                : undefined
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ===== Dropdown orden ===== */
function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState({
    left: 0,
    top: 0,
    width: 220,
  });

  const opts = [
    { v: "name-asc", label: "Nombre (A–Z)" },
    { v: "name-desc", label: "Nombre (Z–A)" },
  ];
  const current =
    opts.find((o) => o.v === value)?.label ?? "Ordenar";

  const recalc = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setAnchor({
      left: Math.round(r.left),
      top: Math.round(r.bottom + 6),
      width: Math.round(r.width || 220),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      recalc();
      setReady(true);
    };
    measure();
    const onScroll = () => measure();
    const onResize = () => measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const toggle = () =>
    setOpen((o) => {
      if (o) setReady(false);
      return !o;
    });

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="h-9 inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 text-white px-3 text-[13px] hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {current}
        <ChevronDown size={16} className="opacity-90" />
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[1000]"
              onClick={toggle}
            />
            <div
              className="fixed z-[1001]"
              style={{
                left: anchor.left,
                top: anchor.top,
                minWidth: anchor.width,
                visibility: ready ? "visible" : "hidden",
              }}
            >
              <div className="rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                {opts.map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => {
                      onChange?.(o.v);
                      toggle();
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-gray-900 ${
                      value === o.v
                        ? "bg-slate-100 font-medium"
                        : ""
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

/* Banner lateral (lista) */
function SectionBanner({ title, subtitle, rightSlot }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow-[0_18px_40px_-22px_rgba(2,6,23,0.45)]">
      <div
        className="px-5 py-4 text-white"
        style={{
          background:
            "linear-gradient(120deg,#0c3b66,#0488AF 80%)",
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-tight truncate">
              {title ?? "Mesas"}
            </h3>
            {subtitle && (
              <p className="text-xs text-white/95 mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
          <div className="relative z-20">{rightSlot}</div>
        </div>
      </div>
    </div>
  );
}

/* Tags */
function TagInput({
  values = [],
  onChange,
  placeholder = "Escribe y presiona Enter…",
}) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  const norm = (s) => String(s || "").trim();
  const key = (s) => norm(s).toLowerCase();

  const addTags = (raw) => {
    const parts = norm(raw)
      .split(/[\n,]+/)
      .map(norm)
      .filter(Boolean);
    if (!parts.length) return;

    const seen = new Set(values.map(key));
    const next = [...values];
    for (const p of parts) {
      const k = key(p);
      if (!seen.has(k)) {
        seen.add(k);
        next.push(p);
      }
    }
    onChange?.(next);
  };

  const removeAt = (i) =>
    onChange?.(values.filter((_, j) => j !== i));

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (norm(inputValue)) {
        addTags(inputValue);
        setInputValue("");
      }
    } else if (
      e.key === "Backspace" &&
      inputValue === "" &&
      values.length > 0
    ) {
      removeAt(values.length - 1);
    }
  };

  const onPaste = (e) => {
    const t = e.clipboardData.getData("text");
    if (t && /[\n,]/.test(t)) {
      e.preventDefault();
      addTags(t);
      setInputValue("");
    }
  };

  const onBlur = () => {
    if (norm(inputValue)) {
      addTags(inputValue);
      setInputValue("");
    }
  };

  return (
    <div
      className="w-full rounded-xl border border-slate-300/80 px-2 py-2 min-h-[44px] flex flex-wrap gap-2 focus-within:ring-2 focus-within:ring-[#0488AF] bg-white text-gray-900"
      onClick={() => inputRef.current?.focus()}
    >
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-slate-50 to-white border border-slate-200 px-2 py-[2px] text-sm text-gray-900"
        >
          <span className="truncate max-w-[180px]">{v}</span>
          <button
            type="button"
            className="p-1 rounded hover:bg-slate-100"
            aria-label={`Quitar ${v}`}
            onClick={() => removeAt(i)}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={onBlur}
        placeholder={values.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[120px] outline-none text-sm px-1 bg-transparent placeholder:text-gray-400 text-gray-900"
      />
    </div>
  );
}

/* ===== Fechas/título ===== */
const isYMD = (v) =>
  /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
const toYMDForInput = (v) => {
  if (!v) return "";
  if (isYMD(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(+d)) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const formatDateSafe = (v) => {
  if (!v) return "";
  if (isYMD(v)) {
    const [, y, m, d] = v.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );
    return `${d}-${m}-${y}`;
  }
  const d = new Date(v);
  if (Number.isNaN(+d)) return String(v);
  return d.toLocaleDateString("es-CL");
};
const stripPdfExt = (s) => String(s || "").replace(/\.pdf$/i, "");
const safeBaseTitle = (s) =>
  String(s || "")
    .replace(/[\\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const fmtDMY = (ymd) => {
  if (!isYMD(ymd)) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}-${m}-${y}`;
};
const ensureDateSuffix = (base, ymd) => {
  const dmy = fmtDMY(ymd);
  if (!dmy) return base;
  if (/\b\d{2}-\d{2}-\d{4}\b/.test(base)) return base;
  return `${base} ${dmy}`;
};
const buildMinuteTitle = ({
  customName,
  fileToUploadName,
  meetingDate,
}) => {
  let base =
    safeBaseTitle(stripPdfExt(customName)) ||
    safeBaseTitle(stripPdfExt(fileToUploadName)) ||
    "Minuta";
  if (meetingDate) base = ensureDateSuffix(base, meetingDate);
  return base;
};

/* ===== Modal de edición de minuta ===== */
function WorktableMinuteModal({
  open,
  initial,
  onClose,
  onSaved,
  worktableId,
}) {
  const [title, setTitle] = useState(initial?.title || "");
  const [date, setDate] = useState(
    toYMDForInput(initial?.meetingDate) || ""
  );
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setDate(toYMDForInput(initial?.meetingDate) || "");
    setFile(null);
  }, [open, initial]);

  const canSave = title.trim() && !saving;

  const onSubmit = async () => {
    if (!canSave) return;

    // Validar que la fecha no sea futura (solo fecha actual hacia atrás)
    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(date + 'T00:00:00');
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        await Swal.fire({
          icon: "warning",
          title: "Fecha inválida",
          text: "No se puede actualizar una minuta con fecha futura. Solo se permiten fechas de hoy hacia atrás.",
        });
        return;
      }
    }

    if (file) {
      if (file.type !== "application/pdf") {
        await Swal.fire({
          icon: "warning",
          title: "Formato inválido",
          text: "Solo PDF.",
        });
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        await Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          text: "Máx 15 MB.",
        });
        return;
      }
    }

    const payload = {
      fileName: title.trim(),
      meetingDate: date || undefined,
      ...(file ? { file } : {}),
    };

    try {
      setSaving(true);
      Swal.fire({
        title: "Guardando…",
        html: file
          ? "Subiendo PDF y actualizando datos…"
          : "Actualizando datos…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      await updateWorktableMinute(
        worktableId,
        initial.id,
        payload
      );

      Swal.close();
      await Swal.fire({
        icon: "success",
        title: "Minuta actualizada",
        timer: 1100,
        showConfirmButton: false,
      });

      onSaved?.();
      onClose?.();
    } catch (e) {
      Swal.close();
      const errorMessage = e?.message || "Error desconocido al actualizar la minuta";
      await Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text: errorMessage,
        confirmButtonText: "Entendido",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[260] bg-black/40 grid place-items-center p-4"
      onMouseDown={(e) =>
        e.target === e.currentTarget && onClose?.()
      }
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl overflow-hidden">
        <div
          className="px-5 py-4 text-white"
          style={{
            background:
              "linear-gradient(90deg,#0c3b66,#0488AF)",
          }}
        >
          <div className="text-lg font-semibold">
            Editar minuta
          </div>
          <div className="text-sm opacity-90">
            Cambia el título/fecha o reemplaza el PDF.
          </div>
        </div>

        <div className="p-5 space-y-4 max-h-[72vh] overflow-y-auto">
          <div>
            <label className="block text-xs text-gray-700">
              Título <span className="text-red-500">*</span>
            </label>
            <input
              className={`mt-1 w-full ${input} placeholder:text-gray-400`}
              value={title}
              onChange={(e) =>
                setTitle(e.target.value)
              }
              placeholder="Ej: Minuta reunión Comité de Innovación"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-700">
              Fecha (opcional)
            </label>
            <input
              type="date"
              className={`mt-1 w-full ${input}`}
              value={date}
              onChange={(e) =>
                setDate(e.target.value)
              }
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-700">
              Reemplazar PDF (opcional)
            </label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) =>
                setFile(e.target.files?.[0] || null)
              }
              className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-[#0488AF] file:px-4 file:py-2 file:text-white hover:file:bg-[#037a98]"
            />
              <div className="mt-1.5 flex items-center gap-1">
                <TooltipHelper text="Si deseas actualizar el PDF de la minuta, selecciona un nuevo archivo. El archivo anterior será reemplazado. Si no seleccionas un archivo, se mantendrá el PDF actual. Máximo 15 MB, solo formato PDF." />
              </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t bg-white flex justify-end gap-2">
          <button className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors" onClick={onClose}>
            Cancelar
          </button>
          <button
            className={`h-11 px-4 rounded-xl text-white text-sm font-semibold transition-colors ${
              canSave ? "bg-emerald-600 hover:bg-emerald-700" : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={onSubmit}
            disabled={!canSave}
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                Guardando…
              </span>
            ) : (
              "Guardar cambios"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== Tarjeta de mesa ===== */
function WorktableCard({ mesa, isActive, onClick }) {
  const participantCount = Array.isArray(mesa.participants)
    ? mesa.participants.length
    : 0;
  const minuteCount = mesa.minutes?.length || 0;

  return (
    <button
      onClick={onClick}
      aria-selected={isActive}
      className={`group relative w-full text-left px-4 py-4 flex items-start justify-between
        rounded-xl border-2 transition-all duration-200
        ${
          isActive
            ? "bg-blue-50/70 border-blue-200 shadow-sm ring-1 ring-blue-200"
            : "bg-white hover:bg-slate-50 border-slate-200 hover:shadow-sm"
        }
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0488AF]`}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1.5 rounded-l-xl"
        style={{
          background:
            "linear-gradient(180deg,#0c3b66,#0488AF)",
        }}
      />
      <div className="min-w-0 pr-3 pl-2 flex-1 overflow-hidden">
        <div className="font-semibold flex items-center gap-2 mb-1">
          <span className="truncate text-gray-900">
            {mesa.name || "(Sin nombre)"}
          </span>
        </div>
        {mesa.description && (
          <div className="mt-1 text-sm text-gray-700 line-clamp-2 break-words overflow-hidden">
            {mesa.description.length > 150 ? `${mesa.description.substring(0, 150)}...` : mesa.description}
          </div>
        )}
        <div className="mt-2 flex items-center flex-wrap gap-2 text-xs text-gray-600">
          {participantCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/80 border border-gray-200 shadow-sm">
              <Users
                size={12}
                className="opacity-70"
              />
              {participantCount}{" "}
              {participantCount === 1
                ? "participante"
                : "participantes"}
            </span>
          )}
          {minuteCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/80 border border-gray-200 shadow-sm">
              <FileText
                size={12}
                className="opacity-70"
              />
              {minuteCount}{" "}
              {minuteCount === 1 ? "minuta" : "minutas"}
            </span>
          )}
          {mesa.organizer && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700">
              <User size={12} />
              Organiza: {mesa.organizer}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
    </button>
  );
}

/* ================= Componente principal ================= */
export default function WorktablesHome() {
  const navigate = useNavigate();

  // Lista
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((i) => {
      const n = String(i.name || "").toLowerCase();
      const d = String(i.description || "").toLowerCase();
      const p = Array.isArray(i.participants)
        ? i.participants.join(" ").toLowerCase()
        : "";
      return (
        n.includes(needle) ||
        d.includes(needle) ||
        p.includes(needle)
      );
    });
  }, [list, q]);

  const ordered = useMemo(() => {
    const arr = [...filtered];
    const col = new Intl.Collator("es", {
      sensitivity: "base",
      numeric: true,
    });
    arr.sort((a, b) =>
      sortBy === "name-asc"
        ? col.compare(a?.name || "", b?.name || "")
        : col.compare(b?.name || "", a?.name || "")
    );
    return arr;
  }, [filtered, sortBy]);

  // Detalle
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState("summary");
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);

  // Ajustar altura al cambio de zoom y resize
  useEffect(() => {
    const updateHeight = () => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);
    // Detectar cambios de zoom
    const mediaQuery = window.matchMedia('(min-resolution: 0.001dpcm)');
    const handleZoom = () => {
      setTimeout(updateHeight, 100);
    };
    window.addEventListener('resize', handleZoom);
    updateHeight();
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
      window.removeEventListener('resize', handleZoom);
    };
  }, []);

  // Minutas
  const [minutes, setMinutes] = useState([]);
  const [minutesLoading, setMinutesLoading] = useState(false);
  const [uploadedFrom, setUploadedFrom] = useState("");
  const [uploadedTo, setUploadedTo] = useState("");

  const minutesFiltered = useMemo(() => {
    let rows = [...minutes];
    if (uploadedFrom) {
      const from = new Date(`${uploadedFrom}T00:00:00`);
      rows = rows.filter(
        (m) => new Date(m.createdAt) >= from
      );
    }
    if (uploadedTo) {
      const to = new Date(`${uploadedTo}T23:59:59`);
      rows = rows.filter(
        (m) => new Date(m.createdAt) <= to
      );
    }
    return rows;
  }, [minutes, uploadedFrom, uploadedTo]);

  // Modal de gestión mesa
  const [manageOpen, setManageOpen] = useState(false);
  const [manageTab, setManageTab] = useState("edit");
  const [isCreating, setIsCreating] = useState(false);

  // Upload
  const [meetingDate, setMeetingDate] = useState("");
  const [customName, setCustomName] = useState("");
  const [fileToUpload, setFileToUpload] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Edit mesa
  const [renameName, setRenameName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [participantsArr, setParticipantsArr] = useState([]);
  const [organizer, setOrganizer] = useState("");
  const [contact, setContact] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);

  // Modal edición de minuta
  const [wmModal, setWmModal] = useState(null);

  /* Effects */
  useEffect(() => {
    (async () => {
      try {
        const data = await listWorktables();
        setList(data);
      } catch (e) {
        Swal.fire("Error", String(e), "error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      manageOpen &&
      !isCreating &&
      manageTab === "edit" &&
      selected
    ) {
      setRenameName(selected.name || "");
      setEditDescription(selected.description || "");
      setParticipantsArr(
        Array.isArray(selected.participants)
          ? selected.participants
          : []
      );
      setOrganizer(selected.organizer || "");
      setContact(selected.contact || "");
    }
  }, [manageOpen, manageTab, selected, isCreating]);

  async function openMesa(id) {
    try {
      const w = await getWorktable(id);
      setSelected(w);
      setDetailTab("summary");
      setUploadedFrom("");
      setUploadedTo("");
      await refreshMinutes(id);
    } catch (e) {
      Swal.fire("Error", String(e), "error");
    }
  }

  async function refreshMinutes(id) {
    setMinutesLoading(true);
    try {
      const rows = await listWorktableMinutes(id);
      setMinutes(rows);
    } catch (e) {
      Swal.fire("Error", String(e), "error");
    } finally {
      setMinutesLoading(false);
    }
  }

  // Crear mesa
  function openCreateModal() {
    setIsCreating(true);
    setManageTab("edit");
    setRenameName("");
    setEditDescription("");
    setParticipantsArr([]);
    setOrganizer("");
    setContact("");
    setManageOpen(true);
  }

  async function saveCreate() {
    const payload = {
      name: (renameName || "").trim(),
      description:
        (editDescription || "").trim() || undefined,
      participants: participantsArr,
      organizer:
        (organizer || "").trim() || undefined,
      contact: (contact || "").trim() || undefined,
    };
    if (!payload.name)
      return Swal.fire(
        "Falta nombre",
        "Ingresa un nombre para la mesa",
        "info"
      );
    try {
      setSavingDetails(true);
      const created = await createWorktable(payload);
      setList((prev) => [created, ...prev]);
      setManageOpen(false);
      setIsCreating(false);
      setSelected(created);
      await refreshMinutes(created.id);
      Swal.fire(
        "Creada",
        "Mesa creada correctamente",
        "success"
      );
    } catch (e) {
      Swal.fire("Error", String(e), "error");
    } finally {
      setSavingDetails(false);
    }
  }

  // Minutas
  async function performUpload() {
    if (!fileToUpload)
      return Swal.fire(
        "Archivo requerido",
        "Selecciona un PDF.",
        "info"
      );
    if (fileToUpload.type !== "application/pdf")
      return Swal.fire(
        "Formato inválido",
        "Debes subir un PDF",
        "info"
      );
    if (fileToUpload.size > 15 * 1024 * 1024)
      return Swal.fire({
        icon: "warning",
        title: "Archivo muy grande",
        html: `El archivo "${fileToUpload.name}" es demasiado grande (máx 15 MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
        confirmButtonText: "Entendido",
      });

    // Validar que la fecha no sea futura (solo fecha actual hacia atrás)
    if (meetingDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(meetingDate + 'T00:00:00');
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        await Swal.fire({
          icon: "warning",
          title: "Fecha inválida",
          text: "No se puede subir una minuta con fecha futura. Solo se permiten fechas de hoy hacia atrás.",
        });
        return;
      }
    }

    const displayTitle = buildMinuteTitle({
      customName,
      fileToUploadName: fileToUpload?.name,
      meetingDate: meetingDate || undefined,
    });

    try {
      setUploading(true);
      Swal.fire({
        title: "Subiendo…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      const rec = await uploadWorktableMinute(
        selected.id,
        fileToUpload,
        {
          meetingDate: meetingDate || undefined,
          fileName: displayTitle,
        }
      );
      Swal.close();
      setMinutes((prev) => [
        { ...rec, title: rec?.title || displayTitle },
        ...prev,
      ]);
      setCustomName("");
      setMeetingDate("");
      setFileToUpload(null);
      if (fileRef.current) fileRef.current.value = "";
      setManageOpen(false);
      setDetailTab("minutes");
      await refreshMinutes(selected.id);
      Swal.fire({
        icon: "success",
        title: "Minuta subida",
        timer: 1100,
        showConfirmButton: false,
      });
    } catch (er) {
      Swal.close();
      Swal.fire("Error", String(er), "error");
    } finally {
      setUploading(false);
    }
  }

  async function onDeleteMinute(mid) {
    const minute = minutes.find((m) => m.id === mid);
    const minuteTitle =
      minute?.title ||
      stripPdfExt(
        minute?.fileName ||
          minute?.originalName ||
          minute?.name ||
          "Minuta"
      );
    const { isConfirmed } = await Swal.fire({
      title: `¿Eliminar minuta "${minuteTitle}"?`,
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    try {
      Swal.fire({
        title: "Eliminando…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      await deleteWorktableMinute(selected.id, mid);
      setMinutes((prev) =>
        prev.filter((m) => m.id !== mid)
      );
      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Minuta eliminada",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.close();
      Swal.fire(
        "Error",
        "No se pudo eliminar la minuta.",
        "error"
      );
    }
  }

  function onEditMinute(minute) {
    setWmModal({ values: minute });
  }

  async function saveDetails() {
    const payload = {
      name: (renameName || "").trim(),
      description:
        (editDescription || "").trim() || undefined,
      participants: participantsArr,
      organizer:
        (organizer || "").trim() || undefined,
      contact: (contact || "").trim() || undefined,
    };
    if (!payload.name)
      return Swal.fire(
        "Falta nombre",
        "Ingresa un nombre para la mesa",
        "info"
      );
    try {
      setSavingDetails(true);
      const updated = await updateWorktable(
        selected.id,
        payload
      );
      setSelected((prev) =>
        prev ? { ...prev, ...updated } : prev
      );
      setList((prev) =>
        prev.map((w) =>
          w.id === updated.id ? { ...w, ...updated } : w
        )
      );
      setManageOpen(false);
      setIsCreating(false);
      Swal.fire(
        "Guardado",
        "La mesa fue actualizada",
        "success"
      );
    } catch (e) {
      Swal.fire("Error", String(e), "error");
    } finally {
      setSavingDetails(false);
    }
  }

  async function onDeleteMesa(id) {
    const mesa = list.find((m) => m.id === id);
    const mesaName = mesa?.name || "esta mesa";
    const ok = (
      await Swal.fire({
        title: `¿Eliminar mesa "${mesaName}"?`,
        text: "Se borrarán también sus minutas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
      })
    ).isConfirmed;
    if (!ok) return;
    try {
      Swal.fire({
        title: "Eliminando…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      await deleteWorktable(id);
      setList((prev) =>
        prev.filter((i) => i.id !== id)
      );
      if (selected?.id === id) {
        setSelected(null);
        setMinutes([]);
      }
      setManageOpen(false);
      Swal.close();
      Swal.fire(
        "Eliminada",
        "Mesa eliminada",
        "success"
      );
    } catch (e) {
      Swal.close();
      Swal.fire("Error", String(e), "error");
    }
  }

  return (
    <div
      className="max-w-[1100px] mx-auto px-4 py-6 space-y-6 text-gray-900 min-w-0 overflow-x-hidden"
      style={{
        background:
          "radial-gradient(80rem 60rem at -20% -10%, rgba(14,165,167,0.06), transparent 40%), radial-gradient(70rem 50rem at 120% -20%, rgba(11,74,143,0.07), transparent 38%)",
      }}
    >
      {/* Banner principal */}
      <div className="relative">
        <div className="px-1 pb-2">
          <ArrowBack />
        </div>
        <Banner
          title="Mesas de Trabajo"
          message="Crea y administra mesas de trabajo, registra minutas."
        />
      </div>

      <SectionTips
        section="las mesas de trabajo"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo usar esta sección? de Mesas de Trabajo"
        tipsList={[
          "En esta sección puedes registrar y revisar mesas de trabajo con información detallada, como descripción, participantes y organizador.",
          "En el recuadro inferior izquierdo está la lista de mesas de trabajo y a la derecha de éste se despliega su detalle, en \"Resumen\" podrás ver la información de la mesa y en \"Minutas\" puedes revisar y registrar reuniones relacionadas.",
          "Para encontrar una mesa puedes usar el buscador, que filtra las palabras en el nombre, la descripción y los participantes.",
          "Puedes editar o eliminar mesas de trabajo y minutas cuando quieras.",
        ]}
        finalNote=""
        color="#0488AF"
        videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766417277/Video_Tutorial_m%C3%B3dulo_Articulaci%C3%B3n_P-P_-_Mesas_de_Trabajo_tkeniw.mp4"
        manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412940/Manual_de_usuario_m%C3%B3dulo_de_articulaci%C3%B3n_P-P_nfoqgo.pdf"
      />

      {/* Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 lg:items-stretch min-w-0">
        {/* Lista */}
        <div className="space-y-4 lg:flex lg:flex-col min-w-0" style={{ height: `${Math.max(viewportHeight * 0.85, 400)}px`, maxHeight: `${viewportHeight * 0.85}px`, minHeight: '400px' }}>
          <SectionBanner
            title="Mesas"
            subtitle={`Total de mesas ${
              list.length > 0 ? `(${list.length})` : "(0)"
            }`}
            rightSlot={
              <div className="flex items-center gap-2 text-sm text-white">
                <span>Ordenar</span>
                <SortDropdown
                  value={sortBy}
                  onChange={setSortBy}
                />
              </div>
            }
          />

          {/* Buscador + Nueva mesa */}
          <PlainCard>
            <div className="p-4 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className={`h-11 w-full rounded-xl pl-9 pr-10 text-sm text-[#09274A] ${input} placeholder:text-gray-400`}
                  placeholder="Buscar por nombre, descripción o participante…"
                  value={q}
                  onChange={(e) =>
                    setQ(e.target.value)
                  }
                />
                {q && (
                  <button
                    type="button"
                    onClick={() => setQ("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-slate-100 text-gray-900"
                    aria-label="Limpiar"
                    title="Limpiar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
                title="Crear mesa"
              >
                <Plus className="w-4 h-4" /> Nueva mesa
              </button>
            </div>
          </PlainCard>

          <GradientCard className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden min-w-0 h-full">
            {loading ? (
              <div className="p-4 space-y-2 overflow-y-auto flex-1">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-16 rounded-xl bg-slate-100/80 animate-pulse"
                  />
                ))}
              </div>
            ) : ordered.length === 0 ? (
              <div className="p-8 text-sm text-center text-gray-600 flex-1 flex items-center justify-center">
                <div>
                  {q ? (
                    <>
                      No hay resultados para{" "}
                      <span className="font-semibold text-gray-900">
                        "{q}"
                      </span>
                      .
                      <div className="mt-3">
                        <button
                          onClick={() => setQ("")}
                          className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors"
                        >
                          <X className="w-4 h-4" /> Limpiar filtro
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl mb-3">📋</div>
                      <p className="font-medium text-gray-900">
                        No hay mesas de trabajo
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Crea tu primera mesa para comenzar
                      </p>
                      <button
                        onClick={openCreateModal}
                        className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors mt-4"
                      >
                        <Plus className="w-4 h-4" /> Crear primera mesa
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto p-2">
                <div className="space-y-2 min-w-0">
                  {ordered.map((m) => (
                    <WorktableCard
                      key={m.id}
                      mesa={m}
                      isActive={selected?.id === m.id}
                      onClick={() => openMesa(m.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </GradientCard>
        </div>

        {/* Detalle */}
        <div className="lg:flex lg:flex-col min-w-0" style={{ height: `${Math.max(viewportHeight * 0.85, 400)}px`, maxHeight: `${viewportHeight * 0.85}px`, minHeight: '400px' }}>
          {!selected ? (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm lg:flex-1 lg:min-h-0 flex items-center justify-center min-w-0 h-full">
              <div className="h-full w-full flex items-center justify-center p-10 text-center min-w-0">
                <div>
                  <div className="text-5xl mb-4 flex justify-center">
                    👥
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Selecciona una mesa
                  </p>
                  <p className="text-xs text-gray-600">
                    Elige una mesa de la lista para ver su
                    información y minutas
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col min-h-0 space-y-0 h-full">
              {/* Banner del título */}
              <div
                className="relative rounded-2xl overflow-hidden shadow-[0_18px_38px_-18px_rgba(2,6,23,0.55)] min-h-[140px] lg:shrink-0 min-w-0"
                style={{
                  background:
                    "linear-gradient(120deg,#0c3b66 0%,#0488AF 100%)",
                }}
              >
                <div className="absolute inset-0 bg-black/5 pointer-events-none" />
                <div className="relative p-4 sm:p-5 text-white h-full flex flex-col min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                    <button
                      onClick={() => setSelected(null)}
                      className="hidden sm:inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-[#C42251] hover:bg-[#A01D45] text-white text-xs font-semibold transition-colors shrink-0"
                    >
                      <ArrowLeft size={14} /> <span className="whitespace-nowrap">Volver</span>
                    </button>
                    <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreating(false);
                          setManageTab("upload");
                          setManageOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-gradient-to-r from-[#09274A] to-[#0488AF] hover:from-[#071f3d] hover:to-[#03779C] text-white text-xs font-semibold shadow transition-colors"
                        title="Subir minuta"
                      >
                        <Upload size={14} /> Subir minuta
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreating(false);
                          setManageTab("edit");
                          setManageOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-gradient-to-r from-[#09274A] to-[#0488AF] hover:from-[#071f3d] hover:to-[#03779C] text-white text-xs font-semibold shadow transition-colors"
                        title="Editar"
                      >
                        <Edit size={14} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          onDeleteMesa(selected.id)
                        }
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-[#C42251] hover:bg-[#A01D45] text-white text-xs font-semibold transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={14} /> Eliminar
                      </button>
                    </div>
                  </div>

                  <h2
                    className="text-lg sm:text-xl font-bold leading-tight break-words mb-1"
                    style={{
                      textShadow:
                        "0 1px 2px rgba(0,0,0,.3)",
                    }}
                  >
                    {selected.name}
                  </h2>

                  <div className="mt-3 flex items-center justify-end">
                    <Segmented
                      value={detailTab}
                      onChange={setDetailTab}
                      options={[
                        { value: "summary", label: "Resumen" },
                        { value: "minutes", label: "Minutas" },
                      ]}
                    />
                  </div>

                  {/* Info rápida */}
                  <div
                    className="absolute bottom-3 left-4 sm:left-5 flex items-center flex-wrap gap-1.5 text-xs min-w-0"
                    // dejamos un espacio fijo a la derecha para el switch Resumen/Minutas
                    style={{ maxWidth: "calc(100% - 190px)" }}
                  >
                    {selected.organizer && (
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm max-w-full min-w-0">
                        <User size={11} className="opacity-90 shrink-0" />
                        <span className="text-white text-[11px] font-medium whitespace-nowrap shrink-0">
                          Organiza:
                        </span>
                        <span className="text-white text-[11px] truncate">
                          {selected.organizer}
                        </span>
                      </div>
                    )}

                    {Array.isArray(selected.participants) &&
                      selected.participants.length > 0 && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm whitespace-nowrap shrink-0">
                          <Users size={11} className="opacity-90 shrink-0" />
                          <span className="text-white text-[11px]">
                            {selected.participants.length}{" "}
                            {selected.participants.length === 1
                              ? "participante"
                              : "participantes"}
                          </span>
                        </div>
                      )}

                    {minutes.length > 0 && (
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm whitespace-nowrap shrink-0">
                        <FileText size={11} className="opacity-90 shrink-0" />
                        <span className="text-white text-[11px]">
                          {minutes.length}{" "}
                          {minutes.length === 1 ? "minuta" : "minutas"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contenido del detalle */}
              <div className="lg:flex-1 lg:min-h-0 lg:max-h-full lg:flex lg:flex-col lg:overflow-hidden" style={{ minHeight: 0, height: '100%', maxHeight: '100%' }}>
                {/* Resumen */}
                {detailTab === "summary" && (
                  <>
                    <div className="h-4 bg-white shrink-0" />
                    <AccentCard
                      titleSlot={
                        <div className="flex items-center gap-2 text-[12px] font-semibold tracking-wide text-white">
                          <FileText size={14} /> Resumen de la
                          mesa
                        </div>
                      }
                      className="lg:flex-1 lg:min-h-0 lg:h-full lg:max-h-full"
                      compactHeader
                    >
                      <div className="p-5 md:p-6 space-y-5 h-full flex flex-col overflow-hidden">
                        <div className="grid md:grid-cols-2 gap-4 flex-1 min-h-0 overflow-hidden">
                          {/* Descripción */}
                          <section className="rounded-xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden flex flex-col h-full min-h-0">
                            <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50/30 border-b border-slate-200 shrink-0">
                              <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                                <FileText
                                  size={16}
                                  className="text-[#0488AF]"
                                />{" "}
                                Descripción
                              </div>
                            </div>
                            <div className="p-4 overflow-y-auto overflow-x-auto min-h-0 flex-1">
                              {selected.description ? (
                                <p className="leading-relaxed whitespace-pre-line break-words text-gray-900 w-full">
                                  {selected.description}
                                </p>
                              ) : (
                                <div className="text-sm text-gray-500 italic w-full">
                                  Sin descripción
                                </div>
                              )}
                            </div>
                          </section>

                          {/* Participantes */}
                          <section className="rounded-xl border-2 border-slate-200 bg-white overflow-hidden flex flex-col h-full min-h-0">
                            <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50/30 border-b border-slate-200 shrink-0">
                              <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                                <Users
                                  size={16}
                                  className="text-[#0488AF]"
                                />{" "}
                                Participantes
                              </div>
                            </div>
                            <div className="p-4 overflow-y-auto overflow-x-auto min-h-0 flex-1">
                              {Array.isArray(
                                selected.participants
                              ) &&
                              selected.participants.length ? (
                                <div className="flex flex-wrap gap-2 w-full">
                                  {selected.participants.map(
                                    (p, i) => {
                                      const isOrganizer =
                                        selected.organizer &&
                                        p ===
                                          selected.organizer;
                                      return (
                                        <span
                                          key={`${p}-${i}`}
                                          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition ${
                                            isOrganizer
                                              ? "ring-2 ring-[#0488AF] bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-300 text-[#0c3b66]"
                                              : "bg-gradient-to-r from-slate-50 to-white border-slate-200 text-gray-900"
                                          }`}
                                          title={
                                            isOrganizer
                                              ? "Organizador/Convocante"
                                              : ""
                                          }
                                        >
                                          {p}{" "}
                                          {isOrganizer &&
                                            "⭐"}
                                        </span>
                                      );
                                    }
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 italic w-full">
                                  Sin participantes
                                  registrados
                                </div>
                              )}
                            </div>
                          </section>
                        </div>

                        {/* Información de contacto */}
                        <section className="rounded-xl border-2 border-slate-200 bg-white overflow-hidden">
                          <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50/30 border-b border-slate-200">
                            <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                              <Mail
                                size={16}
                                className="text-[#0488AF]"
                              />{" "}
                              Información de contacto
                            </div>
                          </div>
                          <div className="p-4 space-y-3">
                            {selected.organizer && (
                              <div className="space-y-1">
                                <div className="text-xs font-medium text-gray-600">
                                  Organizador
                                </div>
                                <div className="text-sm text-gray-900 font-medium">
                                  {selected.organizer}
                                </div>
                              </div>
                            )}
                            {selected.contact && (
                              <div className="space-y-1">
                                <div className="text-xs font-medium text-gray-600">
                                  Contacto
                                </div>
                                <div className="text-sm text-gray-900">
                                  {selected.contact}
                                </div>
                              </div>
                            )}
                            {!selected.organizer &&
                              !selected.contact && (
                                <div className="text-sm text-gray-500 italic">
                                  Sin información de
                                  contacto
                                </div>
                              )}
                          </div>
                        </section>
                      </div>
                    </AccentCard>
                  </>
                )}

                {/* Minutas */}
                {detailTab === "minutes" && (
                  <>
                    <div className="h-4 bg-white shrink-0" />
                    <AccentCard
                      titleSlot={
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-[12px] font-semibold tracking-wide text-white">
                            <FileText size={14} /> Minutas
                          </div>
                          {minutesFiltered.length > 0 && (
                            <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full">
                              {minutesFiltered.length}{" "}
                              {minutesFiltered.length === 1
                                ? "minuta"
                                : "minutas"}
                            </span>
                          )}
                        </div>
                      }
                      className="lg:flex-1 lg:min-h-0 lg:h-full lg:max-h-full"
                      compactHeader={true}
                    >
                    <div className="p-5 md:p-6 h-full flex flex-col overflow-hidden">
                      {/* Filtros - No scroll */}
                      <div className="shrink-0 mb-4">
                        <div className="rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide">
                              Subidas desde
                              <div className="relative mt-2">
                                <input
                                  type="date"
                                  value={uploadedFrom}
                                  onChange={(e) =>
                                    setUploadedFrom(
                                      e.target.value
                                    )
                                  }
                                  className={`h-10 w-full rounded-lg px-3 pr-9 text-sm text-[#09274A] ${input} placeholder:text-gray-400`}
                                />
                                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                              </div>
                            </label>
                            <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide">
                              Subidas hasta
                              <div className="relative mt-2">
                                <input
                                  type="date"
                                  value={uploadedTo}
                                  onChange={(e) =>
                                    setUploadedTo(
                                      e.target.value
                                    )
                                  }
                                  className={`h-10 w-full rounded-lg px-3 pr-9 text-sm text-[#09274A] ${input} placeholder:text-gray-400`}
                                />
                                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Lista minutas - Con scroll */}
                      <div className="flex-1 min-h-0 overflow-y-auto">
                        {minutesLoading ? (
                          <div className="space-y-3">
                            {[...Array(3)].map((_, i) => (
                              <div
                                key={i}
                                className="h-16 rounded-xl bg-slate-100 animate-pulse border border-slate-200"
                              />
                            ))}
                          </div>
                        ) : minutesFiltered.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <FileText size={32} className="mx-auto mb-2 opacity-50"/>
                            <p className="text-sm">No hay minutas</p>
                          </div>
                        ) : (
                          <div className="space-y-3 pr-2">
                            {minutesFiltered.map((m) => {
                              const title =
                                m.title ||
                                stripPdfExt(
                                  m.fileName ||
                                    m.originalName ||
                                    m.name ||
                                    "Minuta"
                                );
                              const url =
                                m.viewUrl || m.secureUrl;
                              return (
                                <div
                                  key={m.id}
                                  className="group p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-blue-200 hover:shadow-md transition-all"
                                >
                                  <div className="flex items-start justify-between gap-3 min-w-0">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                      <div className="h-10 w-10 shrink-0 grid place-items-center rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200">
                                        <FileText
                                          size={20}
                                          className="text-[#0488AF]"
                                        />
                                      </div>
                                      <div className="min-w-0 flex-1 overflow-hidden">
                                        {url ? (
                                          <a
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-semibold hover:underline break-words text-gray-900 block mb-1 min-w-0"
                                          >
                                            {title}
                                          </a>
                                        ) : (
                                          <div className="font-semibold break-words text-gray-900 mb-1 min-w-0">
                                            {title}
                                          </div>
                                        )}
                                        <div className="flex items-center flex-wrap gap-2 text-xs text-gray-600 min-w-0">
                                          {m.meetingDate && (
                                            <span className="inline-flex items-center gap-1 whitespace-nowrap shrink-0">
                                              <Calendar size={12} className="shrink-0" />
                                              <span className="min-w-0">{formatDateSafe(
                                                m.meetingDate
                                              )}</span>
                                            </span>
                                          )}
                                          <span className="inline-flex items-center gap-1 whitespace-nowrap shrink-0">
                                            <FileText size={12} className="shrink-0" />
                                            <span className="shrink-0">Subida:</span>
                                            <span className="min-w-0">{formatDateSafe(
                                              m.createdAt
                                            )}</span>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <button
                                        onClick={() => {
                                          if (url)
                                            window.open(
                                              url,
                                              "_blank",
                                              "noopener,noreferrer"
                                            );
                                        }}
                                        className="h-9 w-9 grid place-items-center rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                        title={
                                          url
                                            ? "Abrir PDF"
                                            : "Sin archivo"
                                        }
                                        disabled={!url}
                                      >
                                        <Eye
                                          size={16}
                                          className="text-gray-700"
                                        />
                                      </button>
                                      <button
                                        className="h-9 w-9 grid place-items-center rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
                                        onClick={() =>
                                          onEditMinute(m)
                                        }
                                        title="Editar"
                                      >
                                        <Edit
                                          size={16}
                                          className="text-white"
                                        />
                                      </button>
                                      <button
                                        className="h-9 w-9 grid place-items-center rounded-lg bg-[#C42251] hover:bg-[#A01D45] transition-colors"
                                        onClick={() =>
                                          onDeleteMinute(m.id)
                                        }
                                        title="Eliminar"
                                      >
                                        <Trash2
                                          size={16}
                                          className="text-white"
                                        />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </AccentCard>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal gestión mesa */}
      {manageOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setManageOpen(false);
              setIsCreating(false);
            }}
            aria-hidden
          />
          <div className="absolute inset-0 grid place-items-center p-6">
            <GradientCard>
              <div className="w-[min(92vw,44rem)] xl:w-[min(88vw,48rem)] rounded-2xl overflow-hidden">
                <div
                  className="px-5 py-4 text-white"
                  style={{
                    background:
                      "linear-gradient(90deg,#0c3b66,#0488AF)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-base sm:text-lg break-words">
                      {isCreating
                        ? "Crear mesa"
                        : `Gestión de "${selected?.name}"`}
                    </div>
                    <button
                      className="p-2 rounded-lg hover:bg-white/10 transition"
                      onClick={() => {
                        setManageOpen(false);
                        setIsCreating(false);
                      }}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-5 max-h-[86vh] overflow-y-auto text-gray-900">
                  {/* Subir minuta */}
                  {!isCreating &&
                    manageTab === "upload" && (
                      <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <label className="flex flex-col text-sm text-gray-900">
                            <span className="inline-flex items-center gap-2 mb-2">
                              <Calendar className="w-4 h-4" />
                              <span>Fecha de reunión</span>
                            </span>
                            <input
                              type="date"
                              value={meetingDate}
                              onChange={(e) =>
                                setMeetingDate(
                                  e.target.value
                                )
                              }
                              max={new Date().toISOString().split('T')[0]}
                              className={`w-full ${input}`}
                            />
                          </label>

                          <label className="flex flex-col text-sm text-gray-900">
                            <span className="mb-2 flex items-center gap-1">
                              Título de la minuta
                              <TooltipHelper text="Si lo dejas vacío, se usará el nombre del archivo sin '.pdf'. El título aparecerá en la lista de minutas y ayudará a identificarla fácilmente." />
                            </span>
                            <input
                              type="text"
                              value={customName}
                              onChange={(e) =>
                                setCustomName(
                                  e.target.value
                                )
                              }
                              className={`w-full ${input} placeholder:text-gray-400`}
                              placeholder="Ej: Minuta reunión proyecto"
                            />
                          </label>
                        </div>

                        <div>
                          <label className="block text-sm text-gray-900 mb-2 flex items-center gap-1">
                            Archivo PDF
                            <TooltipHelper text="Selecciona un archivo PDF de máximo 15 MB. El archivo se subirá y estará disponible para visualización y descarga." />
                          </label>
                          <input
                            ref={fileRef}
                            type="file"
                            accept="application/pdf"
                            onChange={(e) =>
                              setFileToUpload(
                                e.target.files?.[0] || null
                              )
                            }
                            className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                        </div>

                        <div className="pt-2">
                          <button
                            onClick={performUpload}
                            disabled={uploading}
                            className={`inline-flex items-center gap-2 h-11 px-4 rounded-xl text-white text-sm font-semibold transition-colors ${
                              uploading
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-emerald-600 hover:bg-emerald-700"
                            }`}
                          >
                            {uploading ? (
                              "Subiendo…"
                            ) : (
                              <>
                                <Upload className="w-4 h-4" /> Subir
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                  {/* Editar / Crear mesa */}
                  {(isCreating || manageTab === "edit") && (
                    <div className="space-y-4">
                      <label className="block text-sm text-gray-900">
                        Nombre de la mesa{" "}
                        <span className="text-red-500">
                          *
                        </span>
                        <input
                          type="text"
                          value={renameName}
                          onChange={(e) =>
                            setRenameName(e.target.value)
                          }
                          className={`mt-2 w-full ${input} placeholder:text-gray-400`}
                          placeholder='Ej: "Mesa Minería y Energía"'
                        />
                      </label>

                      <label className="block text-sm text-gray-900">
                        Descripción
                        <textarea
                          value={editDescription}
                          onChange={(e) =>
                            setEditDescription(
                              e.target.value
                            )
                          }
                          className={`mt-2 w-full min-h-[120px] whitespace-pre-wrap break-words ${input} placeholder:text-gray-400`}
                          placeholder="Breve descripción de la mesa…"
                        />
                      </label>

                      <label className="block text-sm text-gray-900">
                        <span className="flex items-center gap-1 mb-2">
                          Organizador/Convocante
                          <TooltipHelper text="Este participante será resaltado visualmente en la lista de participantes." />
                        </span>
                        <input
                          type="text"
                          value={organizer}
                          onChange={(e) =>
                            setOrganizer(e.target.value)
                          }
                          className={`mt-2 w-full ${input} placeholder:text-gray-400`}
                          placeholder="Nombre del participante que organiza la mesa"
                        />
                      </label>

                      <div className="block text-sm text-gray-900">
                        <label className="flex items-center gap-1 mb-2">
                          Participantes (Enter para añadir)
                          <TooltipHelper text="Escribe el nombre de cada participante y presiona Enter para agregarlo." />
                        </label>
                        <div className="mt-2">
                          <TagInput
                            values={participantsArr}
                            onChange={setParticipantsArr}
                            placeholder="Ej: SEREMI Economía, CORFO…"
                          />
                        </div>
                      </div>

                      <label className="block text-sm text-gray-900">
                        <span className="flex items-center gap-1 mb-2">
                          Contacto
                          <TooltipHelper text="Puedes incluir correos, teléfonos u otros datos de contacto relevantes." />
                        </span>
                        <input
                          type="text"
                          value={contact}
                          onChange={(e) =>
                            setContact(e.target.value)
                          }
                          className={`mt-2 w-full ${input} placeholder:text-gray-400`}
                          placeholder="Información de contacto"
                        />
                      </label>

                      <div className="-mx-6 px-6 sticky bottom-0 pt-3 pb-2 bg-white/90 backdrop-blur border-t">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (isCreating) {
                                setManageOpen(false);
                                setIsCreating(false);
                              } else {
                                setManageOpen(false);
                              }
                            }}
                            className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors inline-flex items-center gap-2"
                          >
                            <ArrowLeft className="w-4 h-4" /> Volver
                          </button>
                          <button
                            disabled={savingDetails}
                            onClick={
                              isCreating
                                ? saveCreate
                                : saveDetails
                            }
                            className={`inline-flex items-center gap-2 h-11 px-4 rounded-xl text-white text-sm font-semibold transition-colors ${
                              savingDetails
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-emerald-600 hover:bg-emerald-700"
                            }`}
                          >
                            {isCreating ? (
                              <>
                                <Plus className="w-4 h-4" /> Crear
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4" /> Guardar cambios
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </GradientCard>
          </div>
        </div>
      )}

      {/* Modal edición de minuta (reutilizado) */}
      {wmModal && selected && (
        <WorktableMinuteModal
          open={!!wmModal}
          initial={wmModal.values}
          worktableId={selected.id}
          onClose={() => setWmModal(null)}
          onSaved={() => refreshMinutes(selected.id)}
        />
      )}
    </div>
  );
}
